import React, {useEffect, useMemo, useRef, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiEdit2, FiActivity, FiHome, FiInfo, FiLogIn, FiLogOut, FiSearch, FiWifi, FiWifiOff, FiX, FiZap} from "react-icons/fi";
import {GiTurnstile} from "react-icons/gi";
import {MdConfirmationNumber, MdFaceUnlock, MdLan, MdNfc, MdPhoneAndroid, MdQrCodeScanner, MdSmartButton} from "react-icons/md";
import Modal from "../../../components/My/Modal";
import BulkEditModal from "./BulkEditModal";
import {TurnstileButton as Button} from "../components/Button";
import {apiOpenTurniket} from "../Device/Actions";
import Toast from "cogo-toast";
import {useDispatch, useSelector} from "react-redux";
import {getEnigooTurnstileType} from "../enigooTurnstileHelper";
import {RESET_FILTER_TURNSTILE, SET_FILTER_TURNSTILE} from "../../../reducers/filterTurnstile";
import {SET_TURNSTILE_SORT} from "../../../reducers/turnstileSort";
import {useIsMobile} from "../components/Helper";
import TurnstileActionPanel from "../Map/TurnstileActionPanel";
import {apiGetTurnstileDetail, apiSendCommand, apiGetCommands, apiGetZones, apiOpenLegacy} from "../Map/Actions";
import {COMMAND, TURNSTILE_TYPE, usesLegacyOpen, commandErrorMessage} from "../Map/mapConstants";

const PAGE_SIZE = 9;

const ENTRY_TYPES = [
    {value: null, label: "Vše"},
    {value: 1, label: "Vchod"},
    {value: 0, label: "Východ"},
    {value: 2, label: "V areálu"},
];

const ENTRY_CFG = {
    0: {label: "Východ",   Icon: FiLogOut, bg: "#fef2f2", color: "#dc2626", border: "#fecaca"},
    1: {label: "Vchod",    Icon: FiLogIn,  bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0"},
    2: {label: "V areálu", Icon: FiHome,   bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe"},
};

// ─── alert helpers ───────────────────────────────────────────────────────────

const getAlerts = (item) => {
    const a = [];
    if (item.batteryPercent != null && item.batteryTime != null
        && item.batteryPercent < 20 && item.batteryTime < 30) a.push("battery");
    if (item.nfcReader != null && item.nfcReader !== 1) a.push("nfc");
    if (item.barcodeReader != null && item.barcodeReader !== 1) a.push("barcode");
    if (item.networkState != null && item.networkState !== 1) a.push("network");
    if (!item.connected) a.push("offline");
    return a;
};

// higher = worse, used for sorting
const alertScore = (item) => {
    const a = getAlerts(item);
    let s = 0;
    if (a.includes("battery")) s += 8;
    if (a.includes("nfc") || a.includes("barcode") || a.includes("network")) s += 4;
    if (a.includes("offline")) s += 2;
    return s;
};


// ─── řazení ──────────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
    {value: "alerts",    label: "Stav (alerty první)"},
    {value: "id_asc",    label: "ID vzestupně"},
    {value: "id_desc",   label: "ID sestupně"},
    {value: "name_asc",  label: "Název A→Z"},
    {value: "name_desc", label: "Název Z→A"},
    {value: "online",    label: "Online první"},
    {value: "type",      label: "Typ zařízení"},
];

const SORT_FN = {
    alerts:    (a, b) => alertScore(b) - alertScore(a),
    id_asc:    (a, b) => (a.id ?? 0) - (b.id ?? 0),
    id_desc:   (a, b) => (b.id ?? 0) - (a.id ?? 0),
    name_asc:  (a, b) => (a.description || "").localeCompare(b.description || ""),
    name_desc: (a, b) => (b.description || "").localeCompare(a.description || ""),
    online:    (a, b) => (b.connected ? 1 : 0) - (a.connected ? 1 : 0),
    type:      (a, b) => (a.turnstileType ?? 99) - (b.turnstileType ?? 99),
};


// ─── sub-components ───────────────────────────────────────────────────────────

// Connection segmented control — gray bg container, no borders on items
const ConnSegment = ({value, onChange}) => (
    <div style={{background: "#f1f5f9", borderRadius: "999px", padding: "2px", display: "inline-flex", gap: "1px"}}>
        {[{v: null, l: "Vše"}, {v: true, l: "Online"}, {v: false, l: "Offline"}].map(opt => (
            <button
                key={String(opt.v)}
                onClick={() => onChange(opt.v)}
                style={value === opt.v
                    ? {background: "#2563eb", color: "#fff", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, border: "none", cursor: "pointer"}
                    : {background: "transparent", color: "#64748b", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, border: "none", cursor: "pointer"}
                }
            >
                {opt.l}
            </button>
        ))}
    </div>
);

// Individual bordered pill chip for entry type / manufacturer
const FilterChip = ({label, active, onClick}) => (
    <button
        onClick={onClick}
        style={active
            ? {background: "#2563eb", color: "#fff", border: "1px solid #2563eb", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, cursor: "pointer"}
            : {background: "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, cursor: "pointer"}
        }
    >
        {label}
    </button>
);

const ZoneFilter = ({zones, selected, onChange}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = search ? zones.filter(z => z.name.toLowerCase().includes(search.toLowerCase())) : zones;
    const count = selected?.length ?? 0;
    const isActive = count > 0;

    const toggle = (id) => {
        const cur = selected ?? [];
        onChange(cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
    };

    return (
        <div ref={ref} style={{position: "relative"}}>
            <button
                onClick={() => setOpen(v => !v)}
                style={isActive
                    ? {background: "#2563eb", color: "#fff", border: "1px solid #2563eb", borderRadius: "999px", padding: "5px 12px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "5px"}
                    : {background: "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "999px", padding: "5px 12px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: "5px"}
                }
            >
                Zóna{isActive ? ` (${count})` : ""}
                <FiChevronDown size={12} style={{transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s"}}/>
            </button>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 100,
                    background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)", width: "220px", overflow: "hidden",
                }}>
                    <div style={{padding: "8px"}}>
                        <input
                            autoFocus
                            placeholder="Hledat zónu..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{width: "100%", padding: "6px 10px", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "var(--enigoo-fs-sm)", outline: "none", boxSizing: "border-box"}}
                        />
                    </div>
                    <div style={{maxHeight: "220px", overflowY: "auto"}}>
                        {filtered.length === 0
                            ? <p style={{padding: "10px 12px", fontSize: "var(--enigoo-fs-sm)", color: "#94a3b8", margin: 0}}>Žádné výsledky</p>
                            : filtered.map(z => {
                                const checked = selected?.includes(z.id);
                                return (
                                    <button
                                        key={z.id}
                                        onClick={() => toggle(z.id)}
                                        style={{
                                            display: "flex", alignItems: "center", gap: "8px",
                                            width: "100%", padding: "7px 12px", background: checked ? "#eff6ff" : "none",
                                            border: "none", cursor: "pointer", textAlign: "left",
                                            fontSize: "var(--enigoo-fs-sm)", color: checked ? "#2563eb" : "#374151", fontWeight: checked ? 600 : 400,
                                        }}
                                    >
                                        <span style={{
                                            width: "14px", height: "14px", borderRadius: "3px", flexShrink: 0,
                                            border: `2px solid ${checked ? "#2563eb" : "#cbd5e1"}`,
                                            background: checked ? "#2563eb" : "#fff",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            {checked && <span style={{width: "6px", height: "6px", borderRadius: "1px", background: "#fff", display: "block"}}/>}
                                        </span>
                                        {z.name}
                                    </button>
                                );
                            })
                        }
                    </div>
                    {isActive && (
                        <div style={{padding: "6px 8px", borderTop: "1px solid #f1f5f9"}}>
                            <button
                                onClick={() => onChange([])}
                                style={{width: "100%", padding: "5px", background: "none", border: "none", fontSize: "var(--enigoo-fs-xs)", color: "#94a3b8", cursor: "pointer"}}
                            >
                                Zrušit výběr
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const TURNSTILE_TYPE_ICON = {
    0: MdQrCodeScanner,       // VGUANG — QR čtečka
    1: MdNfc,                 // RFID čtečka
    2: GiTurnstile,           // ELVIS turniket
    3: MdSmartButton,         // ELVIS_BUTTON
    4: MdConfirmationNumber,  // TELPO — odbavovací zařízení
    5: GiTurnstile,           // DSN_TURNSTILE
    6: MdPhoneAndroid,        // ENIGOO_READER — android NFC+QR
    7: MdFaceUnlock,          // ENIGOO_FACE_RECOGNITION_READER
    8: MdLan,                 // TICKET_VALIDATOR_SOCKET — odbavovací zařízení (TCP 9878)
};

const CardIcon = ({turnstileType, connected}) => {
    const Icon = TURNSTILE_TYPE_ICON[turnstileType] ?? GiTurnstile;
    return (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            connected ? "bg-blue-50 text-blue-600" : "bg-red-50 text-red-500"
        }`}>
            <Icon size={16}/>
        </div>
    );
};

const ConnBadge = ({connected}) => {
    const Icon = connected ? FiWifi : FiWifiOff;
    return (
        <span
            title={connected ? "Online" : "Offline"}
            style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: "24px", height: "24px", borderRadius: "6px", flexShrink: 0,
                background: connected ? "#f0fdf4" : "#fef2f2",
                color: connected ? "#16a34a" : "#dc2626",
                border: `1px solid ${connected ? "#bbf7d0" : "#fecaca"}`,
            }}
        >
            <Icon size={13}/>
        </span>
    );
};

const InfoRow = ({label, children}) => (
    <div className={"flex items-center justify-between gap-2 text-xs"}>
        <span className={"text-gray-400 flex-shrink-0"}>{label}</span>
        <div className={"text-right"}>{children}</div>
    </div>
);

const BatteryBar = ({percent}) => {
    if (percent == null) return null;
    const color = percent < 20 ? "#ef4444" : percent < 50 ? "#f59e0b" : "#22c55e";
    return (
        <div className={"flex items-center gap-2"}>
            <div style={{flex: 1, height: "4px", background: "#f1f5f9", borderRadius: "999px", overflow: "hidden"}}>
                <div style={{width: `${Math.min(100, percent)}%`, height: "100%", background: color, borderRadius: "999px", transition: "width 0.3s"}}/>
            </div>
            <span style={{fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, color, minWidth: "26px", textAlign: "right"}}>{percent}%</span>
        </div>
    );
};

const StatusDot = ({ok, label}) => (
    <span style={{display: "inline-flex", alignItems: "center", gap: "3px", fontSize: "var(--enigoo-fs-2xs)", fontWeight: 600, color: ok ? "#16a34a" : "#dc2626"}}>
        <span style={{width: "6px", height: "6px", borderRadius: "50%", background: ok ? "#22c55e" : "#ef4444", display: "inline-block"}}/>
        {label}
    </span>
);

const DeviceLogInfo = ({item}) => {
    const isEnigoo = item.turnstileType === 6 || item.turnstileType === 7;
    if (!isEnigoo) return null;
    const hasBattery = item.batteryPercent != null;
    const hasNetwork = item.networkState != null;
    const hasNfc = item.nfcReader != null;
    const hasBarcode = item.barcodeReader != null;
    if (!hasBattery && !hasNetwork && !hasNfc && !hasBarcode) return null;
    return (
        <div style={{marginTop: "4px", paddingTop: "4px", borderTop: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: "4px"}}>
            {hasBattery && (
                <InfoRow label={"Baterie"}>
                    <div style={{minWidth: "120px"}}>
                        <BatteryBar percent={item.batteryPercent}/>
                        {item.batteryTime != null && (
                            <span style={{fontSize: "var(--enigoo-fs-2xs)", color: "#94a3b8", display: "block", textAlign: "right", marginTop: "1px"}}>
                                ~{item.batteryTime} min
                            </span>
                        )}
                    </div>
                </InfoRow>
            )}
            {(hasNfc || hasBarcode || hasNetwork) && (
                <InfoRow label={"Stav"}>
                    <div style={{display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end"}}>
                        {hasNfc && <StatusDot ok={item.nfcReader === 1} label={"NFC"}/>}
                        {hasBarcode && <StatusDot ok={item.barcodeReader === 1} label={"QR"}/>}
                        {hasNetwork && <StatusDot ok={item.networkState === 1} label={"Síť"}/>}
                    </div>
                </InfoRow>
            )}
        </div>
    );
};

const TurnstileCard = ({item, canEdit, selectable = false, selected = false, onToggleSelect, onOpenActions}) => {
    const isMobile = useIsMobile();
    const [expanded, setExpanded] = useState(false);
    const connected = !!item.connected;
    const entryCfg = ENTRY_CFG[item.type];
    const showEntryType = item.turnstileType !== 6 && entryCfg;
    const alerts = getAlerts(item);
    const hasAlert = alerts.length > 0;
    const showDetails = !isMobile || expanded;

    return (
        <div style={{
            background: hasAlert ? "#fff9f9" : "#fff",
            border: `1px solid ${hasAlert ? "#fca5a5" : "#e2e8f0"}`,
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: hasAlert ? "0 1px 4px rgba(239,68,68,0.12)" : "0 1px 3px rgba(0,0,0,0.05)",
            transition: "box-shadow 0.2s",
        }}>
            {/* top stripe */}
            <div style={{height: "2px", width: "100%", background: hasAlert ? "#ef4444" : connected ? "#2563eb" : "#94a3b8"}}/>

            <div style={{padding: "10px 12px"}}>
                {/* header — always visible */}
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", gap: "8px"}}>
                    <div style={{display: "flex", alignItems: "center", gap: "8px", minWidth: 0}}>
                        {selectable && (
                            <input
                                type="checkbox"
                                checked={selected}
                                onChange={onToggleSelect}
                                onClick={e => e.stopPropagation()}
                                style={{width: "16px", height: "16px", flexShrink: 0, cursor: "pointer"}}
                            />
                        )}
                        <CardIcon turnstileType={item.turnstileType} connected={connected}/>
                        <div style={{minWidth: 0}}>
                            {item.description
                                ? <p style={{fontSize: "var(--enigoo-fs-sm)", fontWeight: 700,color: "#1e293b", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>{item.description}</p>
                                : <p style={{fontSize: "var(--enigoo-fs-sm)", fontWeight: 700,color: "#1e293b", margin: 0}}>Bez popisu</p>
                            }
                            <p style={{fontSize: "var(--enigoo-fs-xs)",fontStyle: "italic", color: "#1e293b", fontFamily: "monospace", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>#{item.id}{item.uid ? " · UID: " + item.uid : (item.ipAddress ? " · " + item.ipAddress : "")}</p>
                            {item.uid && item.ipAddress && <p style={{fontSize: "var(--enigoo-fs-2xs)", color: "#94a3b8", fontFamily: "monospace", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>IP: {item.ipAddress}</p>}
                        </div>
                    </div>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: "4px", flexShrink: 0}}>
                        {showEntryType && (
                            <span
                                title={entryCfg.label}
                                style={{
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    width: "24px", height: "24px", borderRadius: "6px",
                                    background: entryCfg.bg, color: entryCfg.color, border: `1px solid ${entryCfg.border}`,
                                }}
                            >
                                <entryCfg.Icon size={13}/>
                            </span>
                        )}
                        <ConnBadge connected={connected}/>
                    </div>
                </div>

                {/* last action — always visible */}
                {item.lastAction && item.lastAction !== "-" && (
                    <div style={{
                        background: "#f8fafc",
                        borderRadius: "5px",
                        padding: "3px 8px",
                        marginBottom: showDetails ? "8px" : "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                    }}>
                        <span style={{fontSize: "var(--enigoo-fs-2xs)", color: "#94a3b8"}}>Poslední akce</span>
                        <span style={{fontSize: "var(--enigoo-fs-2xs)", fontWeight: 600, color: "#475569"}}>{item.lastAction}</span>
                    </div>
                )}

                {/* details — hidden on mobile when collapsed */}
                {showDetails && (
                    <>
                        <div style={{height: "1px", background: "#f1f5f9", margin: "0 -12px 8px"}}/>

                        <div style={{display: "flex", flexDirection: "column", gap: "4px", marginBottom: "8px"}}>
                            <InfoRow label={"Výrobce"}>
                                <span style={{fontWeight: 600, color: "#374151", fontSize: "var(--enigoo-fs-xs)"}}>{getEnigooTurnstileType(item.turnstileType)}</span>
                            </InfoRow>

                            {item.zone?.name && (
                                <InfoRow label={"Zóna"}>
                                    <span style={{background: "#f1f5f9", color: "#4b5563", padding: "1px 6px", borderRadius: "4px", fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700}}>
                                        {item.zone.name}
                                    </span>
                                </InfoRow>
                            )}

                            {item.device?.length > 0 && (
                                <InfoRow label={"Zařízení"}>
                                    <div style={{display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: "3px"}}>
                                        {item.device.map((d, i) => (
                                            <span key={i} style={{background: "#f1f5f9", color: "#4b5563", padding: "1px 6px", borderRadius: "4px", fontSize: "var(--enigoo-fs-2xs)"}}>{d.name}</span>
                                        ))}
                                    </div>
                                </InfoRow>
                            )}

                            {item.linkedTurnstile && (
                                <InfoRow label={"Provázán s"}>
                                    <span style={{fontWeight: 500, color: "#374151", fontSize: "var(--enigoo-fs-xs)"}}>
                                        {item.linkedTurnstile.description || item.linkedTurnstile.ipAddress}
                                    </span>
                                </InfoRow>
                            )}

                            <DeviceLogInfo item={item}/>
                        </div>

                        <div style={{paddingTop: "6px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <div style={{display: "flex", gap: "10px"}}>
                                <span style={{fontSize: "var(--enigoo-fs-xs)"}}>
                                    <span style={{fontWeight: 700, color: "#2563eb"}}>{item.tariff?.length ?? 0}</span>
                                    <span style={{color: "#94a3b8", fontSize: "var(--enigoo-fs-2xs)", marginLeft: "3px"}}>tarif{(item.tariff?.length ?? 0) === 1 ? "" : "y"}</span>
                                </span>
                                <span style={{fontSize: "var(--enigoo-fs-xs)"}}>
                                    <span style={{fontWeight: 700, color: "#2563eb"}}>{item.specialRules?.length ?? 0}</span>
                                    <span style={{color: "#94a3b8", fontSize: "var(--enigoo-fs-2xs)", marginLeft: "3px"}}>pravidel</span>
                                </span>
                            </div>
                            <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                                <span style={{fontSize: "var(--enigoo-fs-2xs)", color: "#cbd5e1", fontFamily: "monospace"}}>#{item.id}</span>
                                {item.turnstileType === 7 && (
                                    <Link to={"/turnstile/history/" + item.id}>
                                        <button style={{display: "flex", alignItems: "center", gap: "3px", color: "#7c3aed", fontWeight: 700, fontSize: "var(--enigoo-fs-xs)", background: "none", border: "none", cursor: "pointer"}}>
                                            <FiActivity size={11}/> Historie
                                        </button>
                                    </Link>
                                )}
                                {item.turnstileType === TURNSTILE_TYPE.TICKET_VALIDATOR_SOCKET && (
                                    <button onClick={() => onOpenActions(item)} style={{display: "flex", alignItems: "center", gap: "3px", color: "#2563eb", fontWeight: 700, fontSize: "var(--enigoo-fs-xs)", background: "none", border: "1px solid #2563eb", borderRadius: "6px", padding: "5px 10px", cursor: "pointer"}}>
                                        <FiZap size={12}/> Akce
                                    </button>
                                )}
                                {canEdit && (
                                    <Link to={"/turnstile/detail/" + item.id}>
                                        <button style={{display: "flex", alignItems: "center", gap: "2px", color: "#fff", fontWeight: 700, fontSize: "var(--enigoo-fs-xs)", background: "#2563eb", border: "1px solid #2563eb", borderRadius: "6px", padding: "5px 10px", cursor: "pointer"}}>
                                            <FiEdit2 size={13}/> Upravit
                                        </button>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </>
                )}

                {/* expand/collapse toggle — mobile only */}
                {isMobile && (
                    <button
                        onClick={() => setExpanded(v => !v)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                            width: "100%",
                            marginTop: "8px",
                            padding: "4px 0",
                            background: "none",
                            border: "none",
                            borderTop: showDetails ? "1px solid #f1f5f9" : "none",
                            color: "#94a3b8",
                            fontSize: "var(--enigoo-fs-2xs)",
                            fontWeight: 600,
                            cursor: "pointer",
                        }}
                    >
                        {expanded
                            ? <><FiChevronUp size={12}/> Skrýt</>
                            : <><FiChevronDown size={12}/> Zobrazit detaily</>
                        }
                    </button>
                )}
            </div>
        </div>
    );
};

const Pagination = ({page, total, pageSize, onChange}) => {
    const totalPages = Math.ceil(total / pageSize);
    if (totalPages <= 1) return null;
    return (
        <nav className={"flex justify-center items-center gap-1.5 mt-6 py-2"}>
            <button
                onClick={() => onChange(page - 1)}
                disabled={page === 1}
                className={"w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-30 transition-colors"}
            >
                <FiChevronLeft size={14}/>
            </button>
            {Array.from({length: totalPages}, (_, i) => i + 1).map(p => (
                <button
                    key={p}
                    onClick={() => onChange(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded text-xs font-semibold transition-all ${
                        p === page
                            ? "bg-blue-600 text-white"
                            : "border border-gray-200 text-gray-600 hover:bg-gray-50"
                    }`}
                >
                    {p}
                </button>
            ))}
            <button
                onClick={() => onChange(page + 1)}
                disabled={page === totalPages}
                className={"w-8 h-8 flex items-center justify-center rounded border border-gray-200 text-gray-400 hover:bg-gray-50 disabled:opacity-30 transition-colors"}
            >
                <FiChevronRight size={14}/>
            </button>
        </nav>
    );
};

// ─── legend ──────────────────────────────────────────────────────────────────

const LegendRow = ({icon, label, bg, color, border}) => (
    <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
        <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: "22px", height: "22px", borderRadius: "6px", flexShrink: 0,
            background: bg, color, border: `1px solid ${border}`,
        }}>
            {icon}
        </span>
        <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569"}}>{label}</span>
    </div>
);

const LegendSection = ({title, children}) => (
    <div>
        <p style={{fontSize: "var(--enigoo-fs-2xs)", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px"}}>{title}</p>
        <div style={{display: "flex", flexDirection: "column", gap: "4px"}}>{children}</div>
    </div>
);

const TurnstileLegend = () => {
    const [open, setOpen] = useState(false);
    return (
        <div style={{marginBottom: "14px"}}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    display: "inline-flex", alignItems: "center", gap: "5px",
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "var(--enigoo-fs-sm)", color: "#64748b", padding: "4px 0", fontWeight: 500,
                }}
            >
                <FiInfo size={13}/>
                Legenda
                <FiChevronDown size={12} style={{transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s"}}/>
            </button>

            {open && (
                <div style={{
                    marginTop: "8px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    background: "#fff",
                    padding: "14px 16px",
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: "16px 24px",
                }}>
                    {/* Typy zařízení */}
                    <LegendSection title="Typy zařízení">
                        <LegendRow icon={<MdQrCodeScanner size={12}/>}      label="QR čtečka (VGUANG)"        bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<MdNfc size={12}/>}                label="RFID čtečka"               bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<GiTurnstile size={12}/>}          label="Turniket (ELVIS / DSN)"    bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<MdSmartButton size={12}/>}        label="Tlačítko (ELVIS)"          bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<MdConfirmationNumber size={12}/>} label="Odbavovací zařízení (TELPO - API)" bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<MdPhoneAndroid size={12}/>}       label="Android čtečka (ENIGOO)"   bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<MdFaceUnlock size={12}/>}         label="Rozpoznání obličeje"       bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<MdLan size={12}/>}                label="Odbavovací zařízení (TELPO - TCP)" bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                    </LegendSection>

                    {/* Stav připojení */}
                    <LegendSection title="Připojení">
                        <LegendRow icon={<MdQrCodeScanner size={12}/>} label="Online zařízení"  bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                        <LegendRow icon={<MdQrCodeScanner size={12}/>} label="Offline zařízení" bg="#fef2f2" color="#dc2626" border="#fecaca"/>
                        <LegendRow icon={<FiWifi size={12}/>}          label="Online"           bg="#f0fdf4" color="#16a34a" border="#bbf7d0"/>
                        <LegendRow icon={<FiWifiOff size={12}/>}       label="Offline"          bg="#fef2f2" color="#dc2626" border="#fecaca"/>
                    </LegendSection>

                    {/* Směr průchodu */}
                    <LegendSection title="Směr průchodu">
                        <LegendRow icon={<FiLogIn size={12}/>}  label="Vchod"    bg="#f0fdf4" color="#16a34a" border="#bbf7d0"/>
                        <LegendRow icon={<FiLogOut size={12}/>} label="Východ"   bg="#fef2f2" color="#dc2626" border="#fecaca"/>
                        <LegendRow icon={<FiHome size={12}/>}   label="V areálu" bg="#eff6ff" color="#2563eb" border="#bfdbfe"/>
                    </LegendSection>

                    {/* Stav karty */}
                    <LegendSection title="Stav karty">
                        <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                            <span style={{width: "22px", height: "12px", borderRadius: "3px", background: "#2563eb", flexShrink: 0, display: "inline-block"}}/>
                            <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569"}}>Online — modrý pruh</span>
                        </div>
                        <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                            <span style={{width: "22px", height: "12px", borderRadius: "3px", background: "#94a3b8", flexShrink: 0, display: "inline-block"}}/>
                            <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569"}}>Offline — šedý pruh</span>
                        </div>
                        <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                            <span style={{width: "22px", height: "12px", borderRadius: "3px", background: "#ef4444", flexShrink: 0, display: "inline-block"}}/>
                            <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569"}}>Alert — červený pruh + červený okraj</span>
                        </div>
                    </LegendSection>

                    {/* Stav zařízení (Enigoo) */}
                    <LegendSection title="Stav čtečky (Enigoo)">
                        <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                            <span style={{width: "8px", height: "8px", borderRadius: "50%", background: "#22c55e", display: "inline-block", flexShrink: 0}}/>
                            <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569"}}>NFC / QR / Síť — OK</span>
                        </div>
                        <div style={{display: "flex", alignItems: "center", gap: "6px"}}>
                            <span style={{width: "8px", height: "8px", borderRadius: "50%", background: "#ef4444", display: "inline-block", flexShrink: 0}}/>
                            <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569"}}>NFC / QR / Síť — problém</span>
                        </div>
                        <div style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569", marginTop: "2px"}}>
                            <span style={{fontWeight: 600, color: "#f59e0b"}}>■</span> Baterie 20–50 % &nbsp;
                            <span style={{fontWeight: 600, color: "#ef4444"}}>■</span> Baterie &lt;20 %
                        </div>
                    </LegendSection>
                </div>
            )}
        </div>
    );
};

// ─── main component ───────────────────────────────────────────────────────────

const TurnstileTable = ({data, onRefresh}) => {
    const [openModal, setOpenModal] = useState(null);
    const [page, setPage] = useState(1);
    const user = useSelector((state) => state.user);
    const filters = useSelector((state) => state.filterTurnstile);
    const sort = useSelector((state) => state.turnstileSort.sort);
    const dispatch = useDispatch();
    const canEdit = !!user?.user?.rights?.enigooTurniketsEdit;

    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkOpen, setBulkOpen] = useState(false);

    const navigate = useNavigate();
    const subjectId = user?.activeAreal?.id;
    const [actionTarget, setActionTarget] = useState(null); // {turnstile} | null
    const [actionCommands, setActionCommands] = useState(null); // array | null
    const [zonesMap, setZonesMap] = useState({}); // {zoneId: name}

    const loadActionCommands = (turnstileId) => {
        apiGetCommands(subjectId, turnstileId,
            (d) => setActionCommands(Array.isArray(d) ? d : []),
            () => setActionCommands([]));
    };

    const onOpenActions = (item) => {
        setActionCommands(null);
        apiGetTurnstileDetail(subjectId, item.id,
            (t) => {
                setActionTarget({turnstile: t || item});
                if ((t || item).turnstileType === TURNSTILE_TYPE.TICKET_VALIDATOR_SOCKET) loadActionCommands(item.id);
            },
            () => setActionTarget({turnstile: item}));
        if (Object.keys(zonesMap).length === 0) {
            apiGetZones(subjectId, (d) => {
                const list = Array.isArray(d) ? d : (d?.zone || []);
                const map = {};
                list.forEach((z) => { if (z && z.id != null) map[z.id] = z.name; });
                setZonesMap(map);
            }, () => {});
        }
    };

    const closeActions = () => { setActionTarget(null); setActionCommands(null); };

    const onActionCommand = (code, payload = {}, onDone) => {
        const t = actionTarget?.turnstile;
        if (!t) { onDone && onDone(false); return; }
        if (!canEdit) {
            Toast.error("Nemáte oprávnění k ovládání turniketů.");
            onDone && onDone(false);
            return;
        }
        const isSocket = t.turnstileType === TURNSTILE_TYPE.TICKET_VALIDATOR_SOCKET;
        const ok = () => {
            Toast.success("Příkaz odeslán.");
            if (isSocket) loadActionCommands(t.id);
            onDone && onDone(true);
        };
        const fail = (errCode) => {
            Toast.error(commandErrorMessage(errCode));
            onDone && onDone(false);
        };
        if (code === COMMAND.OPEN && usesLegacyOpen(t.turnstileType)) {
            apiOpenLegacy(subjectId, t.id, ok, fail);
        } else {
            apiSendCommand(subjectId, t.id, code, payload, ok, fail);
        }
    };

    const toggleSelect = (id) => setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const clearSelection = () => setSelectedIds(new Set());

    useEffect(() => { setPage(1); }, [filters]);

    const setFilter = (patch) => dispatch({type: SET_FILTER_TURNSTILE, payload: patch});
    const resetFilters = () => dispatch({type: RESET_FILTER_TURNSTILE});
    const setSort = (value) => dispatch({type: SET_TURNSTILE_SORT, payload: value});

    const isFiltered = filters.search || filters.connected !== null || filters.entryType !== null || filters.zoneIds?.length > 0 || filters.onlyAlerts;

    const filtered = useMemo(() => {
        if (!data) return [];
        return data
            .filter(item => {
                if (filters.search) {
                    const q = filters.search.toLowerCase();
                    if (!item.description?.toLowerCase().includes(q) &&
                        !item.ipAddress?.toLowerCase().includes(q) &&
                        !item.uid?.toLowerCase().includes(q)) return false;
                }
                if (filters.connected !== null && !!item.connected !== filters.connected) return false;
                if (filters.entryType !== null && item.type !== filters.entryType) return false;
                if (filters.zoneIds?.length > 0 && !filters.zoneIds.includes(item.zone?.id)) return false;
                if (filters.onlyAlerts && getAlerts(item).length === 0) return false;
                return true;
            })
            .sort(SORT_FN[sort] || SORT_FN.alerts);
    }, [data, filters, sort]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    const filteredIds = useMemo(() => filtered.map(i => i.id), [filtered]);
    const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.has(id));
    const selectAllFiltered = () => setSelectedIds(allFilteredSelected ? new Set() : new Set(filteredIds));

    const openTurnstile = () => {
        apiOpenTurniket(user.activeAreal.id, openModal, (response) => {
            if (response.result === "TURNSTILE_WAS_OPEN") Toast.success("Turniket byl otevřen.");
            setOpenModal(null);
        }, () => setOpenModal(null));
    };

    const presentZones = useMemo(() => {
        if (!data) return [];
        const map = new Map();
        data.forEach(d => { if (d.zone?.id) map.set(d.zone.id, d.zone); });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [data]);

    return (
        <>
            <TurnstileLegend/>

            {canEdit && (
                <div style={{display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "14px"}}>
                    <button
                        onClick={selectAllFiltered}
                        style={{background: "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "8px", padding: "6px 12px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 600, cursor: "pointer"}}
                    >
                        {allFilteredSelected ? "Zrušit výběr vyfiltrovaných" : "Vybrat vše vyfiltrované (" + filtered.length + ")"}
                    </button>
                    {selectedIds.size > 0 && (
                        <>
                            <span style={{fontSize: "var(--enigoo-fs-sm)", color: "#475569", fontWeight: 600}}>Vybráno {selectedIds.size}</span>
                            <button
                                onClick={() => setBulkOpen(true)}
                                style={{background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 700, cursor: "pointer"}}
                            >
                                Hromadná změna
                            </button>
                            <button
                                onClick={clearSelection}
                                style={{background: "none", border: "none", color: "#94a3b8", fontSize: "var(--enigoo-fs-sm)", cursor: "pointer"}}
                            >
                                Zrušit výběr
                            </button>
                        </>
                    )}
                </div>
            )}
            {bulkOpen && (
                <BulkEditModal
                    selectedIds={selectedIds}
                    close={() => setBulkOpen(false)}
                    onSaved={() => { clearSelection(); onRefresh && onRefresh(); }}
                />
            )}

            {/* ── Filter bar ── */}
            <div style={{marginBottom: "20px"}}>
                {/* search */}
                <div style={{position: "relative", marginBottom: "10px"}}>
                    <FiSearch size={15} style={{position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none"}}/>
                    <input
                        type={"text"}
                        placeholder={"Hledat podle popisu, IP nebo UID..."}
                        value={filters.search}
                        onChange={e => setFilter({search: e.target.value})}
                        style={{width: "100%", padding: "10px 14px 10px 38px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "var(--enigoo-fs-lg)", background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box"}}
                    />
                </div>

                {/* chips row */}
                <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px"}}>
                    <ConnSegment value={filters.connected} onChange={v => setFilter({connected: v})}/>

                    <div style={{width: "1px", height: "20px", background: "#e2e8f0", margin: "0 2px"}}/>

                    {ENTRY_TYPES.map(t => (
                        <FilterChip
                            key={String(t.value)}
                            label={t.label}
                            active={filters.entryType === t.value}
                            onClick={() => setFilter({entryType: t.value})}
                        />
                    ))}

                    {presentZones.length > 0 && (
                        <>
                            <div style={{width: "1px", height: "20px", background: "#e2e8f0", margin: "0 2px"}}/>
                            <ZoneFilter
                                zones={presentZones}
                                selected={filters.zoneIds}
                                onChange={zoneIds => setFilter({zoneIds})}
                            />
                        </>
                    )}

                    <div style={{width: "1px", height: "20px", background: "#e2e8f0", margin: "0 2px"}}/>
                    <button
                        onClick={() => setFilter({onlyAlerts: !filters.onlyAlerts})}
                        style={filters.onlyAlerts
                            ? {background: "#fee2e2", color: "#dc2626", border: "1px solid #fca5a5", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "5px"}
                            : {background: "#fff", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: "999px", padding: "5px 14px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: "5px"}
                        }
                    >
                        <span style={{width: "7px", height: "7px", borderRadius: "50%", background: filters.onlyAlerts ? "#dc2626" : "#94a3b8", display: "inline-block", flexShrink: 0}}/>
                        Jen s alertem
                    </button>

                    <div style={{width: "1px", height: "20px", background: "#e2e8f0", margin: "0 2px"}}/>
                    <label style={{display: "flex", alignItems: "center", gap: "6px", fontSize: "var(--enigoo-fs-sm)", color: "#64748b"}}>
                        Řadit
                        <select
                            value={sort}
                            onChange={e => setSort(e.target.value)}
                            style={{background: "#fff", color: "#475569", border: "1px solid #cbd5e1", borderRadius: "999px", padding: "5px 12px", fontSize: "var(--enigoo-fs-sm)", fontWeight: 500, cursor: "pointer", outline: "none"}}
                        >
                            {SORT_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </label>

                    {isFiltered && (
                        <button
                            onClick={resetFilters}
                            style={{marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "var(--enigoo-fs-sm)", display: "flex", alignItems: "center", gap: "4px"}}
                        >
                            <FiX size={12}/> Zrušit filtry
                        </button>
                    )}
                </div>

                {isFiltered && (
                    <p style={{fontSize: "var(--enigoo-fs-sm)", color: "#94a3b8", marginTop: "8px"}}>
                        Nalezeno <strong style={{color: "#475569"}}>{filtered.length}</strong> z {data?.length ?? 0}
                    </p>
                )}
            </div>

            {/* ── Cards ── */}
            {paged.length === 0 ? (
                <div className={"py-16 text-center text-gray-400"}>
                    <FiSearch size={32} className={"mx-auto mb-2 opacity-30"}/>
                    <p className={"text-sm"}>Žádné výsledky neodpovídají filtrům</p>
                </div>
            ) : (
                <div className={"grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"}>
                    {paged.map(item => (
                        <TurnstileCard
                            key={item.id}
                            item={item}
                            canEdit={canEdit}
                            selectable={canEdit}
                            selected={selectedIds.has(item.id)}
                            onToggleSelect={() => toggleSelect(item.id)}
                            onOpenActions={onOpenActions}
                        />
                    ))}
                </div>
            )}

            <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage}/>

            <Modal
                title={"Otevřít turniket"}
                body={<h3>Opravdu chcete otevřít turniket?</h3>}
                visible={openModal}
                close={() => setOpenModal(null)}
                footer={<>
                    <Button text={"Otevřít"} onClick={openTurnstile}/>
                    <Button text={"Zrušit"} onClick={() => setOpenModal(null)}/>
                </>}
            />

            {actionTarget && (
                <Modal
                    title={"Akce turniketu"}
                    visible={!!actionTarget}
                    close={closeActions}
                    body={
                        <div style={{width: "100%"}}>
                            <TurnstileActionPanel
                                turnstile={actionTarget.turnstile}
                                onAction={onActionCommand}
                                onDetail={() => navigate("/turnstile/detail/" + actionTarget.turnstile.id)}
                                commands={actionCommands}
                                zoneName={actionTarget.turnstile?.zone != null ? zonesMap[actionTarget.turnstile.zone] : null}
                                bg={"transparent"}/>
                        </div>
                    }
                />
            )}
        </>
    );
};

export default TurnstileTable;
